import React, { useState, useRef, useEffect } from 'react';
import { DriverFormData, FormStep } from '../types/driver';
import PersonalInfoStep from './steps/PersonalInfoStep';
import NextOfKinStep from './steps/NextOfKinStep';
import LicenseStep from './steps/LicenseStep';
import VehicleStep from './steps/VehicleStep';
import BankingStep from './steps/BankingStep';
import BackgroundStep from './steps/BackgroundStep';
import ReviewStep from './steps/ReviewStep';
import PasswordStep from './steps/PasswordStep';
import SuccessStep from './steps/SuccessStep';
import { driverApi } from '../services/driverApi';

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

interface UploadedFileUrls {
  licenseFront: string | null;
  licenseBack: string | null;
  profilePhoto: string | null;
  passportPhoto: string | null;
  vehiclePhotos: string[];
  insuranceCertificate: string | null;
  roadworthinessCertificate: string | null;
}

const DriverVerificationForm: React.FC = () => {
  const getUrlPhone = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('phone');
  };

  const getUrlVerificationToken = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('verification_token') || '';
  };

  const [currentStep, setCurrentStep] = useState<FormStep>('personal');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [, setIsSubmitting] = useState(false);
  const progressContainerRef = useRef<HTMLDivElement>(null);

const [formData, setFormData] = useState<DriverFormData>({
    phone: getUrlPhone() || '',
    phoneVerificationToken: getUrlVerificationToken(), 
    fullName: '',
    dateOfBirth: '',
    email: '',
    address: '',
    nationalId: '',
    nin: '',
    stateOfOrigin: '',
    nextOfKinName: '',
    nextOfKinPhone: '',
    nextOfKinRelationship: '',
    licenseNumber: '',
    licenseExpiry: '',
    drivingExperience: '',
    licenseFront: null,
    licenseBack: null,
    vehicleRegistration: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleColor: '',
    vehicleYear: '',
    vehiclePhotos: [],
    bankName: '',
    accountNumber: '',
    accountName: '',
    criminalRecordCheck: false,
    drivingRecordCheck: false,
    profilePhoto: null,
    passportPhoto: null,
    insuranceCertificate: null,
    roadworthinessCertificate: null,
    password: '',
    confirmPassword: '',
  });

  const [uploadedFileUrls, setUploadedFileUrls] = useState<UploadedFileUrls>({
    licenseFront: null,
    licenseBack: null,
    profilePhoto: null,
    passportPhoto: null,
    vehiclePhotos: [],
    insuranceCertificate: null,
    roadworthinessCertificate: null,
  });

  const steps: { key: FormStep; title: string }[] = [
    { key: 'personal', title: 'Personal Info' },
    { key: 'nextofkin', title: 'Next of Kin' },
    { key: 'license', title: 'Driver License' },
    { key: 'vehicle', title: 'Vehicle Info' },
    { key: 'banking', title: 'Banking Info' },
    { key: 'background', title: 'Documents' },
    { key: 'review', title: 'Review' },
    { key: 'password', title: 'Create Password' },
  ];

  useEffect(() => {
    if (progressContainerRef.current) {
      const currentIndex = steps.findIndex((s) => s.key === currentStep);
      const stepElements = progressContainerRef.current.children;
      if (stepElements[currentIndex]) {
        stepElements[currentIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const uploadFiles = async (
    files: File[],
    type: keyof UploadedFileUrls
  ): Promise<void> => {
    try {
      const response = await driverApi.uploadFiles(files);
      const urls = response.uploaded_urls;

      setUploadedFileUrls((prev) => {
        if (type === 'vehiclePhotos') {
          return { ...prev, vehiclePhotos: [...prev.vehiclePhotos, ...urls] };
        }
        return { ...prev, [type]: urls[0] };
      });

      if (type === 'vehiclePhotos') {
        setFormData((prev) => ({
          ...prev,
          vehiclePhotos: [...prev.vehiclePhotos, ...files],
        }));
      } else {
        setFormData((prev) => ({ ...prev, [type]: files[0] }));
      }
    } catch (error: any) {
      alert(`File upload failed: ${error.message}`);
      throw error;
    }
  };

  const removeVehiclePhoto = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      vehiclePhotos: prev.vehiclePhotos.filter((_, i) => i !== index),
    }));
    setUploadedFileUrls((prev) => ({
      ...prev,
      vehiclePhotos: prev.vehiclePhotos.filter((_, i) => i !== index),
    }));
  };

  const transformFormDataForApi = (data: DriverFormData) => ({
    phone: data.phone,
    full_name: data.fullName,
    email: data.email,
    date_of_birth: new Date(data.dateOfBirth).toISOString(),
    address: data.address,
    national_id: data.nationalId,
    nin: data.nin,
    state_of_origin: data.stateOfOrigin,
    next_of_kin_name: data.nextOfKinName,
    next_of_kin_phone: data.nextOfKinPhone,
    next_of_kin_relationship: data.nextOfKinRelationship,
    license_number: data.licenseNumber,
    license_expiry: new Date(data.licenseExpiry).toISOString(),
    driving_experience: data.drivingExperience,
    license_front_url: uploadedFileUrls.licenseFront,
    license_back_url: uploadedFileUrls.licenseBack,
    vehicle_model: data.vehicleModel,
    license_plate: data.vehicleRegistration,
    vehicle_registration: data.vehicleRegistration,
    vehicle_make: data.vehicleMake,
    vehicle_color: data.vehicleColor,
    vehicle_year: data.vehicleYear,
    vehicle_photos: uploadedFileUrls.vehiclePhotos,
    bank_name: data.bankName,
    account_number: data.accountNumber,
    account_name: data.accountName,
    criminal_record_check: data.criminalRecordCheck,
    driving_record_check: data.drivingRecordCheck,
    profile_photo_url: uploadedFileUrls.profilePhoto,
    passport_photo_url: uploadedFileUrls.passportPhoto,
    insurance_certificate_url: uploadedFileUrls.insuranceCertificate,
    roadworthiness_certificate_url: uploadedFileUrls.roadworthinessCertificate,
    password: data.password,
    confirm_password: data.confirmPassword,
    phone_verification_token: data.phoneVerificationToken, 
  });

  const validateStep = (step: FormStep): boolean => {
    switch (step) {
      case 'personal':
        return !!(
          formData.fullName &&
          formData.dateOfBirth &&
          formData.email &&
          formData.address &&
          formData.nationalId &&
          formData.nin &&
          formData.stateOfOrigin
        );
      case 'nextofkin':
        return !!(
          formData.nextOfKinName &&
          formData.nextOfKinPhone &&
          formData.nextOfKinRelationship
        );
      case 'license':
        return !!(
          formData.licenseNumber &&
          formData.licenseExpiry &&
          formData.drivingExperience &&
          uploadedFileUrls.licenseFront &&
          uploadedFileUrls.licenseBack
        );
      case 'vehicle':
        return !!(
          formData.vehicleRegistration &&
          formData.vehicleMake &&
          formData.vehicleModel &&
          formData.vehicleColor &&
          formData.vehicleYear &&
          uploadedFileUrls.vehiclePhotos.length > 0
        );
      case 'banking':
        return !!(
          formData.bankName &&
          formData.accountNumber &&
          formData.accountName
        );
      case 'background':
        return !!(
          formData.criminalRecordCheck &&
          formData.drivingRecordCheck &&
          uploadedFileUrls.profilePhoto &&
          uploadedFileUrls.passportPhoto
        );
      case 'review':
        return true;
      case 'password':
        return !!(
          formData.password &&
          formData.confirmPassword &&
          formData.password === formData.confirmPassword
        );
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (!validateStep(currentStep)) {
      alert('Please fill in all required fields before proceeding.');
      return;
    }
    const currentIndex = steps.findIndex((s) => s.key === currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1].key);
    }
  };

  const prevStep = () => {
    const currentIndex = steps.findIndex((s) => s.key === currentStep);
    if (currentIndex > 0) setCurrentStep(steps[currentIndex - 1].key);
  };

  const goToStep = (step: FormStep) => {
    const stepIndex = steps.findIndex((s) => s.key === step);
    const currentIndex = steps.findIndex((s) => s.key === currentStep);
    if (stepIndex <= currentIndex) setCurrentStep(step);
  };

  const updateFormData = (data: Partial<DriverFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const handleSubmit = () => {
    const allValid = steps.slice(0, steps.length - 2).every((s) =>
      validateStep(s.key)
    );
    if (!allValid) {
      alert('Please complete all required fields in all steps before submitting.');
      return;
    }
    setCurrentStep('password');
  };

  const handleFinalSubmit = async () => {
    if (!formData.password || !formData.confirmPassword) {
      alert('Please create a password before submitting.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match.');
      return;
    }

    const passwordChecks = [
      { test: formData.password.length >= 8, msg: 'Password must be at least 8 characters long' },
      { test: /[A-Z]/.test(formData.password), msg: 'Password must contain at least one uppercase letter' },
      { test: /[a-z]/.test(formData.password), msg: 'Password must contain at least one lowercase letter' },
      { test: /\d/.test(formData.password), msg: 'Password must contain at least one number' },
      { test: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password), msg: 'Password must contain at least one special character' },
    ];
    for (const check of passwordChecks) {
      if (!check.test) { alert(check.msg); return; }
    }

    if (!uploadedFileUrls.licenseFront) { alert('Please upload the front of your license.'); return; }
    if (!uploadedFileUrls.licenseBack) { alert('Please upload the back of your license.'); return; }
    if (uploadedFileUrls.vehiclePhotos.length === 0) { alert('Please upload at least one vehicle photo.'); return; }
    if (!uploadedFileUrls.profilePhoto) { alert('Please upload your profile photo.'); return; }
    if (!uploadedFileUrls.passportPhoto) { alert('Please upload your passport photograph.'); return; }

    try {
      setIsSubmitting(true);
      const apiData = transformFormDataForApi(formData);
      await driverApi.registerDriver(apiData);
      setIsSubmitted(true);
    } catch (error: any) {
      alert(`Registration failed: ${error.message || 'Please try again'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReturnToApp = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage('verification_complete');
    } else {
      alert('Verification complete! You can now return to the Xryde app and login.');
    }
  };

  const renderStep = () => {
    if (isSubmitted) return <SuccessStep onReturnToApp={handleReturnToApp} />;

    switch (currentStep) {
      case 'personal':
        return <PersonalInfoStep data={formData} updateData={updateFormData} />;
      case 'nextofkin':
        return <NextOfKinStep data={formData} updateData={updateFormData} />;
      case 'license':
        return (
          <LicenseStep
            data={formData}
            updateData={updateFormData}
            onFileUpload={uploadFiles}
            uploadedUrls={uploadedFileUrls}
          />
        );
      case 'vehicle':
        return (
          <VehicleStep
            data={formData}
            updateData={updateFormData}
            onFileUpload={uploadFiles}
            uploadedUrls={uploadedFileUrls}
            onRemoveVehiclePhoto={removeVehiclePhoto}
          />
        );
      case 'banking':
        return <BankingStep data={formData} updateData={updateFormData} />;
      case 'background':
        return (
          <BackgroundStep
            data={formData}
            updateData={updateFormData}
            onFileUpload={uploadFiles}
            uploadedUrls={uploadedFileUrls}
          />
        );
      case 'review':
        return <ReviewStep data={formData} onSubmit={handleSubmit} />;
      case 'password':
        return (
          <PasswordStep
            data={formData}
            updateData={updateFormData}
            onSubmit={handleFinalSubmit}
            onBack={() => setCurrentStep('review')}
          />
        );
      default:
        return null;
    }
  };

  const currentStepIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {!isSubmitted && currentStep !== 'password' && (
          <>
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Complete Driver Verification
              </h1>
              <p className="text-gray-600">
                Please complete all steps to become a verified Xryde driver
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="overflow-x-auto pb-2 -mx-2 px-2">
                <div
                  ref={progressContainerRef}
                  className="flex items-center justify-between min-w-max space-x-2"
                >
                  {steps.map((step, index) => {
                    const isCompleted = index < currentStepIndex;
                    const isCurrent = index === currentStepIndex;
                    const isClickable = index <= currentStepIndex;

                    return (
                      <div
                        key={step.key}
                        className={`flex items-center flex-shrink-0 transition-all duration-200 ${isCurrent ? 'scale-105' : ''}`}
                      >
                        <button
                          onClick={() => isClickable && goToStep(step.key)}
                          disabled={!isClickable}
                          className={`flex items-center text-left ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                        >
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center border-2 flex-shrink-0 transition-all duration-200 ${
                              isCompleted
                                ? 'bg-green-500 border-green-500 text-white'
                                : isCurrent
                                ? 'bg-blue-500 border-blue-500 text-white ring-2 ring-blue-200'
                                : 'border-gray-300 text-gray-500 bg-white'
                            }`}
                          >
                            {isCompleted ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              index + 1
                            )}
                          </div>
                          <span
                            className={`ml-2 font-medium whitespace-nowrap transition-colors duration-200 ${
                              isCompleted
                                ? 'text-green-600'
                                : isCurrent
                                ? 'text-blue-600 font-semibold'
                                : 'text-gray-500'
                            }`}
                          >
                            {step.title}
                          </span>
                        </button>
                        {index < steps.length - 1 && (
                          <div
                            className={`w-8 md:w-12 h-0.5 mx-2 md:mx-3 flex-shrink-0 transition-colors duration-200 ${
                              isCompleted ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4 text-center">
                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  Step {currentStepIndex + 1} of {steps.length}: {steps[currentStepIndex]?.title}
                </span>
              </div>
            </div>
          </>
        )}

        <div className={`bg-white rounded-lg shadow-sm p-6 ${isSubmitted ? 'text-center' : ''}`}>
          {renderStep()}
          {!isSubmitted && currentStep !== 'password' && (
            <div className="mt-8 pt-6 border-t">
              <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <button
                  onClick={prevStep}
                  disabled={currentStep === 'personal'}
                  className={`w-full sm:w-auto px-6 py-3 sm:py-2 rounded-md font-medium transition-colors duration-200 ${
                    currentStep === 'personal'
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-500 text-white hover:bg-gray-600'
                  }`}
                >
                  Previous
                </button>

                <div className="hidden sm:block text-sm text-gray-500 text-center">
                  {currentStep === 'review'
                    ? 'Review your information before submitting'
                    : validateStep(currentStep)
                    ? 'All required fields are filled'
                    : 'Please fill all required fields'}
                </div>

                {currentStep === 'review' ? (
                  <button
                    onClick={handleSubmit}
                    className="w-full sm:w-auto px-6 py-3 sm:py-2 bg-green-500 text-white rounded-md font-medium hover:bg-green-600 transition-colors duration-200"
                  >
                    Submit Verification
                  </button>
                ) : (
                  <button
                    onClick={nextStep}
                    className="w-full sm:w-auto px-6 py-3 sm:py-2 bg-blue-500 text-white rounded-md font-medium hover:bg-blue-600 transition-colors duration-200"
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverVerificationForm;